import { FractalEngine, AnimatedFractal } from './fractal-engine.js';
import StatsClient from './StatsClient.js';
import { CanvasRenderer } from './canvas-renderer.js';
import { CanvasInteractionHandler } from './canvas-interactions.js';
import { UIControls } from './ui-controls.js';
import { defaultConfig, CONSTRAINTS, PERFORMANCE_LIMITS } from './config.js';
import { Square } from './math-utils.js';
import { SVGRenderer } from './svg-renderer.js';
import { domCache, DOMUtils } from './dom-cache.js';
import { i18n } from './i18n.js';
import { languageToggle } from './language-toggle.js';
import { seoManager } from './seo-manager.js';

/**
 * Main Pythagoras Trees application
 */
class PythagorasTreeApp {
    /**
     * Create application instance
     */
    constructor() {
        // Configuration
        this.config = { ...defaultConfig };

        // Initialize stats client
        this.statsClient = new StatsClient({
            toolName: 'pythagorastree',
            toolVersion: '1.0.0',
            apiKey: 'abc123pec*os456xyz',
            apiUrl: 'https://matematica.ch/api/feedbackData.php',
            debug: false,
        });

        // Core components
        this.canvas = null;
        this.engine = null;
        this.renderer = null;
        this.interactions = null;
        this.uiControls = null;
        this.animatedFractal = null; // Application state
        this.currentSquares = [];
        this.currentTriangles = [];
        this.isAnimating = false;

        this.initialize();
    }

    /**
     * Initialize the application asynchronously
     */
    async initialize() {
        try {
            await this.initializeComponents();
            this.bindEvents();
            this.generateInitialTree();
        } catch (error) {
            console.error('Failed to initialize application:', error);
        }
    }

    /**
     * Initialize all application components
     */
    async initializeComponents() {
        // Initialize i18n system first
        await i18n.initialize();

        // Initialize SEO manager and update meta tags for current language
        seoManager.updateMetaTags(i18n.getCurrentLanguage());

        // Listen for language changes to update SEO
        i18n.onLanguageChange(newLang => {
            seoManager.updateMetaTags(newLang);
        });

        // Initialize DOM cache
        domCache.initialize();

        // Get canvas element from cache
        this.canvas = domCache.get('fractal-canvas');
        if (!this.canvas) {
            throw new Error('Canvas element not found');
        }

        // Create core components
        this.engine = new FractalEngine(this.config);
        this.renderer = new CanvasRenderer(this.canvas, this.config);

        // Set up renderer redraw callback to ensure tree remains visible
        this.renderer.setRedrawCallback(() => {
            if (this.currentSquares && this.currentSquares.length > 0) {
                this.redrawCurrent();
            }
        });

        // Create interaction handler
        this.interactions = new CanvasInteractionHandler(this.canvas, this.renderer, this.config);

        // Create animated fractal controller
        this.animatedFractal = new AnimatedFractal(this.engine, this.renderer, this.config);

        // Create UI controls
        this.uiControls = new UIControls(this.config, {
            onParameterChange: this.onParameterChange.bind(this),
            onPresetLoad: this.onPresetLoad.bind(this),
            onAnimationStart: this.onAnimationStart.bind(this),
            onAnimationStop: this.onAnimationStop.bind(this),
            onExport: this.onExport.bind(this),
            onLayoutChange: () => {
                // Handle layout changes (canvas resize, etc.)
            },
            onRedrawRequired: () => {
                // CRITICAL: Redraw the tree after any resize event
                if (this.currentSquares && this.currentSquares.length > 0) {
                    this.redrawCurrent();
                }
            },
        });

        // Initialize language toggle
        await languageToggle.initialize();

        // Register for language changes to update UI
        i18n.onLanguageChange(() => {
            this.updateUILanguage();
        });

        // Setup gallery link to preserve language
        this.setupGalleryLink();

        // Setup page lifecycle handlers
        this.setupPageLifecycleHandlers();
    }

    /**
     * Bind application-level events
     */
    bindEvents() {
        // Canvas interaction redraw callback
        this.interactions.setRedrawCallback(() => {
            this.redrawCurrent();
        });

        // Animation completion callback
        this.animatedFractal.setCallbacks({
            onComplete: () => {
                this.isAnimating = false;
            },
        });

        // Window events
        window.addEventListener('beforeunload', () => {
            this.cleanup();
        });
    }

    /**
     * Generate and display initial fractal tree
     */
    generateInitialTree() {
        try {
            const baseSquare = this.createBaseSquare();
            this.generateTree(baseSquare);
        } catch (error) {
            console.error('Failed to generate initial tree:', error);
            this.showError('Failed to generate fractal tree');
        }
    }

    /**
     * Create base square for fractal generation
     * @returns {Square} Base square object
     */
    createBaseSquare() {
        // Position base square so it sits at the bottom with tree growing upward
        const centerX = 0; // World coordinates center
        const centerY = 0; // Center the base square at world origin

        return new Square(
            centerX,
            centerY,
            this.config.baseSquareSize,
            0, // No rotation for base square
            0 // Level 0
        );
    } /**
     * Generate complete fractal tree
     * @param {Square} baseSquare - Starting square
     */
    generateTree(baseSquare) {
        // Show loading indicator
        this.showLoadingIndicator(true);

        try {
            // Generate tree with both squares and triangles
            const treeData = this.engine.generateTreeWithTriangles(
                baseSquare,
                this.config.triangleAngle,
                this.config.recursionDepth,
                this.config.endWithTriangles
            );

            this.currentSquares = treeData.squares;
            this.currentTriangles = treeData.triangles;

            // Render the tree with both squares and triangles
            this.renderer.renderTree(this.currentSquares, this.currentTriangles, this.config);
        } catch (error) {
            console.error('Tree generation failed:', error);
            this.showError('Failed to generate tree');
        } finally {
            this.showLoadingIndicator(false);
        }
    }

    /**
     * Generate triangles for visualization (fixed implementation)
     * @param {Square} baseSquare - Starting square
     * @returns {Array} Array of triangles used in construction
     */
    generateTrianglesForDebugging() {
        const triangles = [];
        const allSquares = this.currentSquares; // Use the already generated squares

        // Generate triangles for all squares except the last level
        allSquares.forEach(square => {
            if (square.level < this.config.recursionDepth) {
                const triangle = this.engine.createTriangleOnSquare(
                    square,
                    this.config.triangleAngle
                );
                triangles.push(triangle);
            }
        });

        return triangles;
    }

    /**
     * Start animated tree generation
     * @param {Square} baseSquare - Starting square
     */
    async startAnimation(baseSquare) {
        if (this.isAnimating) {
            this.animatedFractal.stopAnimation();
        }

        this.isAnimating = true;

        try {
            await this.animatedFractal.startLevelByLevelAnimation(
                baseSquare,
                this.config.triangleAngle,
                this.config.recursionDepth
            );
        } catch (error) {
            console.error('Animation failed:', error);
            this.isAnimating = false;
        }
    } /**
     * Redraw current tree (for interactions)
     */
    redrawCurrent() {
        if (this.currentSquares.length > 0) {
            this.renderer.renderTree(this.currentSquares, this.currentTriangles, this.config);
        }
    }

    /**
     * Parameter change handler
     * @param {string} paramName - Parameter name
     * @param {*} value - New value
     */
    onParameterChange(paramName, value) {
        // Validate parameter
        if (!this.validateParameter(paramName, value)) {
            console.warn(`Invalid parameter value: ${paramName} = ${value}`);
            return;
        }

        // Special handling for recursion depth to prevent excessive square generation
        if (paramName === 'recursionDepth') {
            const adjustedDepth = this.validateAndAdjustRecursionDepth(value);
            if (adjustedDepth !== value) {
                value = adjustedDepth;
                // Update the UI to reflect the adjusted value
                if (this.uiControls && this.uiControls.updateUIControl) {
                    this.uiControls.updateUIControl('recursionDepth', adjustedDepth);
                }
            }
        }

        // Update configuration
        this.config[paramName] = value;

        // Regenerate tree if not animating
        if (!this.isAnimating) {
            const baseSquare = this.createBaseSquare();
            this.generateTree(baseSquare);
        }
    }

    /**
     * Preset load handler
     * @param {string} presetKey - Preset key
     * @param {Object} presetData - Preset data
     */
    onPresetLoad(presetKey, presetData) {
        // Stop current animation
        if (this.isAnimating) {
            this.animatedFractal.stopAnimation();
            this.isAnimating = false;
        }

        // Generate new tree with preset
        const baseSquare = this.createBaseSquare();
        this.generateTree(baseSquare);
    }

    /**
     * Animation start handler
     */
    onAnimationStart() {
        const baseSquare = this.createBaseSquare();
        this.startAnimation(baseSquare);
    }

    /**
     * Animation stop handler
     */
    onAnimationStop() {
        if (this.isAnimating) {
            this.animatedFractal.stopAnimation();
            this.isAnimating = false;
        }
    }

    /**
     * Export handler
     * @param {string} format - Export format
     * @param {Object} options - Export options
     */
    onExport(format, options) {
        // Send stats (fire and forget - non-blocking)
        this.statsClient.sendStats({
            event: 'download',
            language: i18n.getCurrentLanguage(),
            angle: this.config.triangleAngle,
            // eslint-disable-next-line camelcase
            image_type: format,
        });

        try {
            if (format === 'png') {
                this.exportPNG(options);
            } else if (format === 'svg') {
                this.exportSVG(options);
            }
        } catch (error) {
            console.error('Export failed:', error);
            this.showError('Export failed');
        }
    }

    /**
     * Export as PNG with automatic cropping
     * @param {Object} options - Export options
     */
    exportPNG(options) {
        const dataUrl = this.renderer.exportCanvasCropped(
            this.currentSquares,
            this.currentTriangles,
            'png',
            options.quality || 1.0
        );
        this.downloadFile(dataUrl, 'pythagoras-tree.png');
    } /**
     * Export as SVG
     * @param {Object} options - Export options
     */
    exportSVG(options) {
        try {
            // Create SVG renderer
            const svgRenderer = new SVGRenderer(this.config);

            // Set default options from config
            const svgOptions = {
                ...this.config.exportSVG,
                ...options,
            };

            // Export the current tree data as SVG
            const svgContent = svgRenderer.exportTreeAsSVG(
                this.currentSquares,
                this.currentTriangles,
                svgOptions.width,
                svgOptions.height,
                svgOptions
            );

            // Download the SVG file
            this.downloadSVGFile(svgContent, 'pythagoras-tree.svg');
        } catch (error) {
            console.error('SVG export failed:', error);
            this.showError('SVG export failed: ' + error.message);
        }
    } /**
     * Download file from data URL
     * @param {string} dataUrl - Data URL
     * @param {string} filename - Filename
     */
    downloadFile(dataUrl, filename) {
        const link = document.createElement('a');
        link.download = filename;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    /**
     * Download SVG file from content
     * @param {string} svgContent - SVG markup content
     * @param {string} filename - Filename
     */
    downloadSVGFile(svgContent, filename) {
        const blob = new Blob([svgContent], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.download = filename;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL
        URL.revokeObjectURL(url);
    }

    /**
     * Validate parameter value
     * @param {string} paramName - Parameter name
     * @param {*} value - Value to validate
     * @returns {boolean} True if valid
     */
    validateParameter(paramName, value) {
        switch (paramName) {
            case 'triangleAngle':
                return value >= CONSTRAINTS.MIN_ANGLE && value <= CONSTRAINTS.MAX_ANGLE;
            case 'recursionDepth':
                return value >= CONSTRAINTS.MIN_DEPTH && value <= CONSTRAINTS.MAX_DEPTH;
            case 'baseSquareSize':
                return value >= CONSTRAINTS.MIN_BASE_SIZE && value <= CONSTRAINTS.MAX_BASE_SIZE;
            case 'animationSpeed':
                return (
                    value >= CONSTRAINTS.MIN_ANIMATION_SPEED &&
                    value <= CONSTRAINTS.MAX_ANIMATION_SPEED
                );
            case 'evenColor':
            case 'oddColor':
            case 'backgroundColor':
                return /^#[0-9A-F]{6}$/i.test(value);
            default:
                return true;
        }
    }

    /**
     * Validate and adjust recursion depth to prevent excessive square generation
     * @param {number} requestedDepth - Requested recursion depth
     * @returns {number} Adjusted recursion depth
     */
    validateAndAdjustRecursionDepth(requestedDepth) {
        // Estimate number of squares that would be generated
        // In a binary tree (Pythagoras tree), level n has approximately 2^n squares
        // Total squares up to level n ≈ 2^(n+1) - 1
        const estimatedSquares = Math.pow(2, requestedDepth + 1) - 1;
        // Check against performance limit
        if (estimatedSquares > PERFORMANCE_LIMITS.MAX_VISIBLE_SQUARES) {
            // Calculate maximum safe depth
            // 2^(maxDepth+1) - 1 = MAX_VISIBLE_SQUARES
            // 2^(maxDepth+1) = MAX_VISIBLE_SQUARES + 1
            // maxDepth + 1 = log2(MAX_VISIBLE_SQUARES + 1)
            // maxDepth = log2(MAX_VISIBLE_SQUARES + 1) - 1
            const maxSafeDepth = Math.floor(
                Math.log2(PERFORMANCE_LIMITS.MAX_VISIBLE_SQUARES + 1) - 1
            );
            // Ensure we don't go below minimum constraint
            const adjustedDepth = Math.max(maxSafeDepth, CONSTRAINTS.MIN_DEPTH);
            // Show user-friendly message about the adjustment
            this.showInfo(
                `Recursion depth adjusted to ${adjustedDepth} to maintain performance (would generate ~${estimatedSquares.toLocaleString()} squares)`
            );

            return adjustedDepth;
        }

        // Also check against maximum constraint
        const constrained = Math.min(requestedDepth, CONSTRAINTS.MAX_DEPTH);

        return constrained;
    }

    /**
     * Show/hide loading indicator
     * @param {boolean} show - Whether to show indicator
     */
    showLoadingIndicator(show) {
        const indicator = domCache.get('loading-indicator');
        if (indicator) {
            DOMUtils.toggleClass('loading-indicator', 'hidden', !show);
        }
    }

    /**
     * Show error message
     * @param {string} message - Error message
     */
    showError(message) {
        // For now, just console.error and alert
        console.error(message);
        alert(message);
    }

    /**
     * Show informational message
     * @param {string} message - Info message
     */
    showInfo(message) {
        // You could also show this in a toast notification or status bar
        // For now, we'll use a less intrusive console log
    }

    /**
     * Update UI language when language changes
     */
    updateUILanguage() {
        // Update HTML lang attribute
        i18n.updateHtmlLang();

        // Update document title
        const title = i18n.t('meta.title');
        if (title && title !== 'meta.title') {
            document.title = title;
        }

        // Update meta description
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            const description = i18n.t('meta.description');
            if (description && description !== 'meta.description') {
                metaDesc.setAttribute('content', description);
            }
        }

        // Trigger UI controls to update their text
        if (this.uiControls) {
            this.uiControls.updateLanguageTexts();
        }
    }

    /**
     * Setup gallery link to preserve current language
     */
    setupGalleryLink() {
        const galleryLink = document.querySelector('.gallery-link');
        if (galleryLink) {
            galleryLink.addEventListener('click', e => {
                e.preventDefault();
                const currentLang = i18n.getCurrentLanguage();
                const url = new URL(galleryLink.href, window.location.origin);
                if (currentLang !== 'en') {
                    url.searchParams.set('lang', currentLang);
                }
                window.location.href = url.toString();
            });
        }
    }

    /**
     * Setup page lifecycle handlers to reinitialize interactions when needed
     */
    setupPageLifecycleHandlers() {
        // Handle page show (when returning from bfcache)
        window.addEventListener('pageshow', event => {
            if (event.persisted) {
                // Page was restored from cache, reinitialize interactions
                this.reinitializeInteractions();
            }
        });

        // Handle visibility change (when tab becomes visible again)
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                // Give a small delay to ensure DOM is ready
                setTimeout(() => {
                    this.checkAndReinitializeInteractions();
                }, 100);
            }
        });

        // Handle focus events as a fallback
        window.addEventListener('focus', () => {
            setTimeout(() => {
                this.checkAndReinitializeInteractions();
            }, 100);
        });
    }

    /**
     * Check if interactions are working and reinitialize if needed
     */
    checkAndReinitializeInteractions() {
        // Only reinitialize if there's actually a problem
        if (!this.areInteractionsWorking()) {
            this.reinitializeInteractions();
        }
    }

    /**
     * Test if canvas interactions are working properly
     * @returns {boolean} True if interactions are working
     */
    areInteractionsWorking() {
        // Check if basic components exist
        if (!this.canvas || !this.interactions) {
            return false;
        }

        // Check if canvas is properly attached to DOM
        if (!document.contains(this.canvas)) {
            return false;
        }

        // Check if the interaction handler is properly configured
        try {
            // Test if the interaction handler has the required properties and methods
            return (
                this.interactions.canvas === this.canvas &&
                this.interactions.boundHandlers &&
                typeof this.interactions.boundHandlers.mouseDown === 'function' &&
                this.interactions.renderer === this.renderer
            );
        } catch (error) {
            console.warn('Error checking interactions:', error);
            return false;
        }
    }

    /**
     * Reinitialize canvas interactions
     */
    reinitializeInteractions() {
        try {
            if (this.interactions) {
                // Destroy existing interactions
                this.interactions.destroy();
            }

            // Recreate interactions
            this.interactions = new CanvasInteractionHandler(
                this.canvas,
                this.renderer,
                this.config
            );

            // Set up the redraw callback
            this.interactions.setRedrawCallback(() => {
                this.redrawCurrent();
            });
        } catch (error) {
            console.error('Failed to reinitialize canvas interactions:', error);
            // Fallback: ensure interactions is null to prevent future issues
            this.interactions = null;
        }
    }

    /**
     * Cleanup resources
     */
    cleanup() {
        if (this.interactions) {
            this.interactions.destroy();
        }

        if (this.animatedFractal) {
            this.animatedFractal.stopAnimation();
        }
    }
}

/**
 * Initialize application when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
    try {
        window.pythagorasApp = new PythagorasTreeApp();
    } catch (error) {
        console.error('Failed to start application:', error);
        alert('Failed to start application. Please check browser compatibility.');
    }
});

/**
 * Export for global access
 */
export { PythagorasTreeApp };
